/**
 * HoodDeskLogo — security shield + checkmark mark used across the admin shell.
 * Pairs with the `Wordmark` for full brand-lockup. Inline SVG so it renders
 * before any font/asset network round-trip.
 */
import * as React from 'react';
import { cn } from '@/lib/utils';

interface LogoMarkProps extends React.SVGProps<SVGSVGElement> {
  size?: number;
  /** "tone" controls the fill — `brand` uses the gradient, `mono` inherits currentColor. */
  tone?: 'brand' | 'mono';
}

export function LogoMark({ size = 28, tone = 'brand', className, ...rest }: LogoMarkProps): JSX.Element {
  const id = React.useId();
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 64 64"
      className={cn('shrink-0', className)}
      aria-hidden="true"
      role="img"
      {...rest}
    >
      {tone === 'brand' ? (
        <defs>
          <linearGradient id={`vt-grad-${id}`} x1="0" y1="0" x2="1" y2="1">
            <stop offset="0" stopColor="#0d3b66" />
            <stop offset="1" stopColor="#2c5985" />
          </linearGradient>
        </defs>
      ) : null}
      <rect
        width="64"
        height="64"
        rx="14"
        fill={tone === 'brand' ? `url(#vt-grad-${id})` : 'currentColor'}
      />
      <path
        d="M32 9 L52 17 V32 C52 44 44 52 32 56 C20 52 12 44 12 32 V17 Z"
        fill="none"
        stroke="#ffffff"
        strokeWidth={2.5}
        strokeLinejoin="round"
        opacity={0.9}
      />
      <path
        d="M22 30 L31 39 L43 24"
        fill="none"
        stroke="#ffffff"
        strokeWidth={3.2}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

interface WordmarkProps extends React.HTMLAttributes<HTMLSpanElement> {
  /** when true, the wordmark is hidden via screen-reader-only — useful in collapsed sidebars. */
  srOnly?: boolean;
}

export function Wordmark({ className, srOnly, ...rest }: WordmarkProps): JSX.Element {
  return (
    <span
      className={cn(
        'font-semibold text-vt-text tracking-tight',
        srOnly ? 'sr-only' : '',
        className
      )}
      {...rest}
    >
      HoodDesk
    </span>
  );
}

interface LogoLockupProps {
  size?: number;
  collapsed?: boolean;
  className?: string;
}

export function LogoLockup({ size = 28, collapsed = false, className }: LogoLockupProps): JSX.Element {
  return (
    <div className={cn('flex items-center gap-vt-2', className)}>
      <LogoMark size={size} />
      <Wordmark srOnly={collapsed} className="text-vt-16" />
    </div>
  );
}
